/**
 * WebGL shader background - procedural aurora/fire effect.
 * GPU-accelerated, zero CPU overhead after init.
 */

const VERTEX = `
  attribute vec2 a_position;
  void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
`;

const FRAGMENT = `
  precision mediump float;
  uniform float u_time;
  uniform vec2 u_resolution;

  float noise(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  float smoothNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = noise(i);
    float b = noise(i + vec2(1.0, 0.0));
    float c = noise(i + vec2(0.0, 1.0));
    float d = noise(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 5; i++) {
      v += a * smoothNoise(p);
      p *= 2.0;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;
    float t = u_time * 0.15;

    float n1 = fbm(uv * 3.0 + vec2(t * 0.3, t * 0.1));
    float n2 = fbm(uv * 2.0 + vec2(-t * 0.2, t * 0.4));

    float mask = smoothstep(0.0, 0.7, uv.y) * smoothstep(1.0, 0.5, uv.y);
    float intensity = (n1 * 0.6 + n2 * 0.4) * mask * 0.25;

    vec3 gold = vec3(0.788, 0.659, 0.298);
    vec3 amber = vec3(0.831, 0.529, 0.176);
    vec3 col = mix(gold, amber, n2) * intensity;

    gl_FragColor = vec4(col, 1.0);
  }
`;

export function initShaderBg(canvas: HTMLCanvasElement): () => void {
  if (window.innerWidth < 768) return () => {};

  const glMaybe = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false });
  if (!glMaybe) return () => {};
  const gl: WebGLRenderingContext = glMaybe;

  function compileShader(src: string, type: number): WebGLShader | null {
    const s = gl.createShader(type);
    if (!s) return null;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  const vs = compileShader(VERTEX, gl.VERTEX_SHADER);
  const fs = compileShader(FRAGMENT, gl.FRAGMENT_SHADER);
  if (!vs || !fs) return () => {};

  const prog = gl.createProgram();
  if (!prog) return () => {};
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return () => {};

  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

  const aPos = gl.getAttribLocation(prog, 'a_position');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const uTime = gl.getUniformLocation(prog, 'u_time');
  const uRes = gl.getUniformLocation(prog, 'u_resolution');

  let running = true;
  let raf = 0;

  function resize() {
    canvas.width = canvas.offsetWidth * 0.5;
    canvas.height = canvas.offsetHeight * 0.5;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(uRes, canvas.width, canvas.height);
  }

  function tick(time: number) {
    if (!running) return;
    gl.uniform1f(uTime, time * 0.001);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    raf = requestAnimationFrame(tick);
  }

  function onVisibility() {
    if (document.hidden) {
      cancelAnimationFrame(raf);
    } else {
      raf = requestAnimationFrame(tick);
    }
  }

  resize();
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', onVisibility);
  raf = requestAnimationFrame(tick);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
